import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, Stack, Select, NumberInput, Text, Badge, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEye, IconPlus, IconTrash } from '@tabler/icons-react';
import { usePriceWatches, useAddPriceWatch, useRemovePriceWatch, usePriceWatchAlerts, useMarkPriceAlertRead } from '@/hooks/usePortalApi';

export default function PriceWatch() {
  const { data: watchData } = usePriceWatches();
  const { data: alertData } = usePriceWatchAlerts();
  const addWatch = useAddPriceWatch();
  const removeWatch = useRemovePriceWatch();
  const markRead = useMarkPriceAlertRead();
  const [opened, { open, close }] = useDisclosure(false);
  const [productId, setProductId] = useState<number | ''>('');
  const [watchType, setWatchType] = useState('both');
  const [threshold, setThreshold] = useState<number | ''>('');

  const watches = watchData?.watches ?? [];
  const alerts = alertData?.alerts ?? [];
  const unreadCount = alerts.filter(a => !a.is_read).length;

  const handleAdd = () => {
    if (!productId) return;
    addWatch.mutate({ product_id: Number(productId), watch_type: watchType, threshold_pct: threshold ? Number(threshold) : undefined }, {
      onSuccess: () => { close(); notifications.show({ title: 'Added', message: 'Price watch added', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Price Watch</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>Watch Product</Button>
      </Group>

      <Tabs defaultValue="watches">
        <Tabs.List>
          <Tabs.Tab value="watches">My Watches ({watches.length})</Tabs.Tab>
          <Tabs.Tab value="alerts" rightSection={unreadCount > 0 ? <Badge size="xs" color="red">{unreadCount}</Badge> : null}>
            Alerts
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="watches" pt="md">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Current Price</Table.Th>
                <Table.Th>Watch Type</Table.Th>
                <Table.Th>Threshold</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {watches.map((w) => (
                <Table.Tr key={w.id}>
                  <Table.Td>{w.product_name} <Text size="xs" c="dimmed">{w.product_sku}</Text></Table.Td>
                  <Table.Td>${w.current_price.toFixed(2)}</Table.Td>
                  <Table.Td><Badge variant="light">{w.watch_type}</Badge></Table.Td>
                  <Table.Td>{w.threshold_pct ? `${w.threshold_pct}%` : 'Any change'}</Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="subtle" color="red" onClick={() => removeWatch.mutate(w.product_id)}>
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel value="alerts" pt="md">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Change</Table.Th>
                <Table.Th>Old Price</Table.Th>
                <Table.Th>New Price</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {alerts.map((a) => (
                <Table.Tr key={a.id} style={{ fontWeight: a.is_read ? 'normal' : 'bold' }}>
                  <Table.Td>{a.product_name}</Table.Td>
                  <Table.Td>
                    <Badge color={a.change_type === 'decrease' ? 'green' : 'red'}>
                      {a.change_type === 'decrease' ? '-' : '+'}{a.change_pct}%
                    </Badge>
                  </Table.Td>
                  <Table.Td>${a.old_price.toFixed(2)}</Table.Td>
                  <Table.Td>${a.new_price.toFixed(2)}</Table.Td>
                  <Table.Td>{a.effective_date ?? '-'}</Table.Td>
                  <Table.Td>
                    {!a.is_read && (
                      <Button size="xs" variant="subtle" leftSection={<IconEye size={14} />}
                        onClick={() => markRead.mutate(a.id)}>Mark Read</Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>
      </Tabs>

      <Modal opened={opened} onClose={close} title="Watch Product Price">
        <Stack>
          <NumberInput label="Product ID" value={productId} onChange={(v) => setProductId(typeof v === 'number' ? v : '')} />
          <Select label="Watch Type" data={[{ value: 'decrease', label: 'Price Decrease' }, { value: 'increase', label: 'Price Increase' }, { value: 'both', label: 'Both' }]}
            value={watchType} onChange={(v) => setWatchType(v ?? 'both')} />
          <NumberInput label="Threshold (%)" value={threshold} onChange={(v) => setThreshold(typeof v === 'number' ? v : '')} decimalScale={2} />
          <Button onClick={handleAdd} loading={addWatch.isPending}>Add Watch</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
